import { i18n } from '@lingui/core';
import { formatAmount, formatDurationAsHMS, formatDurationAsMS, formatEuro, formatPercentage } from '../../common/formatting';
import { MyGameDailyStatsQuery, MyGameStatsQuery } from '../App/Content/UserDeveloper/Submission/submission-graphql';
import { DeveloperGameAnalytics } from '@crazygames/graph-types/game/developer-analytics.type';
import moment from 'moment';
import { NON_RELEASED_STATUSES } from '../App/Content/UserDeveloper/Submission/UpdateSubmission/UpdateSubmission.types';
import { SUBMISSION_STATUS } from '@crazygames/game-types/src/submission/submission-status.type';
import { BasicLaunchPerformanceIndicators, BasicLaunchStatsAndBenchmarks } from '@crazygames/stats-types/stats.types';
import {
  convertPeriodToOption,
  convertPeriodToPreviousPeriodOption,
  timespansPerMonth,
  GRAPHQL_DATETIME_FORMAT,
} from '../../common/datetime';

// TABS
export const NO_METRICS_STATUSES: SUBMISSION_STATUS[] = [...NON_RELEASED_STATUSES, 'PREPARING_RELEASE'];
export type MetricsCategories = 'basic_launch' | 'engagement' | 'performance' | 'monetization';
export interface MetricsTabs {
  isVisible: boolean;
  id: MetricsCategories;
  label: string;
  metrics: Metrics[];
  filters: {
    device: boolean;
    time: boolean;
  };
  notes?: JSX.Element | undefined;
}
export const setDefaultSelectedTab = (basicLaunchOn: string | null, basicLaunchExpiresOn: string | null) => {
  if (!basicLaunchOn || !basicLaunchExpiresOn) {
    return 'engagement';
  }

  const TODAY = moment.now();
  const basicLaunchOnTimestamp = moment(basicLaunchOn, 'YYYY-MM-DD HH:mm:ss').valueOf();
  const basicLaunchOffTimestamp = moment(basicLaunchExpiresOn, 'YYYY-MM-DD HH:mm:ss').valueOf();

  return TODAY >= basicLaunchOnTimestamp && TODAY <= basicLaunchOffTimestamp ? 'basic_launch' : 'engagement';
};

export type DeviceFilterOptionsValues = 'desktop' | 'mobile' | 'all';
export type DeviceFilterOption = {
  value: DeviceFilterOptionsValues;
  label: string;
};
export const renderDeviceFilterLabelHelper = (selectedFilter: DeviceFilterOptionsValues) => {
  let device = '';

  switch (selectedFilter) {
    case 'mobile':
      device = 'mobile only';
      break;
    case 'desktop':
      device = 'desktop only';
      break;
    case 'all':
    default:
      device = 'all devices';
  }

  return device;
};
export const DEVICE_FILTER_OPTIONS: DeviceFilterOption[] = [
  {
    value: 'all',
    label: 'All',
  },
  {
    value: 'desktop',
    label: 'Desktop',
  },
  {
    value: 'mobile',
    label: 'Mobile',
  },
];

export type MetricsPeriodValues = // StatsPeriodValue | used for querys
  'YESTERDAY' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'LAST_6_MONTHS' | 'ALL_TIME' | 'CUSTOM';
export interface MetricsOptions {
  from: string;
  until: string;
}
export interface StatsPeriod {
  period: MetricsPeriodValues;
  options?: MetricsOptions;
  preDefinedTimeframe: boolean;
}
export interface MetricsTimeframeInput {
  category: 'default' | 'preset' | 'custom';
  period: MetricsPeriodValues;
  options: MetricsOptions;
  preDefinedTimeframe: boolean;
}
export type TimeframeFilterOption = {
  value: MetricsTimeframeInput;
  label: string;
};
export const DEFAULT_TIMEFRAME_FILTER: MetricsTimeframeInput = {
  category: 'default',
  period: 'LAST_6_MONTHS',
  options: convertPeriodToOption('LAST_6_MONTHS'),
  preDefinedTimeframe: true,
};
export const ALL_TIME_TIMEFRAME_FILTER: MetricsTimeframeInput = {
  category: 'default',
  period: 'ALL_TIME',
  options: convertPeriodToOption('ALL_TIME'),
  preDefinedTimeframe: true,
};

export function renderTimeframeFilterOptions(customPeriod: MetricsOptions): TimeframeFilterOption[] {
  const defaultTimeframes: TimeframeFilterOption[] = [
    {
      value: {
        category: 'default',
        period: 'YESTERDAY',
        options: convertPeriodToOption('YESTERDAY'),
        preDefinedTimeframe: true,
      },
      label: 'Yesterday',
    },
    {
      value: {
        category: 'default',
        period: 'LAST_7_DAYS',
        options: convertPeriodToOption('LAST_7_DAYS'),
        preDefinedTimeframe: true,
      },
      label: 'Last 7 Days',
    },
    {
      value: {
        category: 'default',
        period: 'LAST_30_DAYS',
        options: convertPeriodToOption('LAST_30_DAYS'),
        preDefinedTimeframe: true,
      },
      label: 'Last 30 Days',
    },
    {
      value: {
        category: 'default',
        period: 'LAST_6_MONTHS',
        options: convertPeriodToOption('LAST_6_MONTHS'),
        preDefinedTimeframe: true,
      },
      label: 'Last 6 Months',
    },
    {
      value: {
        category: 'default',
        period: 'ALL_TIME',
        options: convertPeriodToOption('ALL_TIME'),
        preDefinedTimeframe: true,
      },
      label: 'All time',
    },
    {
      value: {
        category: 'custom',
        period: 'CUSTOM',
        options: customPeriod,
        preDefinedTimeframe: true,
      },
      label: 'Custom',
    },
  ];
  const predefinedTimeframes: TimeframeFilterOption[] = timespansPerMonth(6).map((timespan) => {
    const { from, until } = timespan.toGraphInputTimespan();
    return {
      value: {
        category: 'preset',
        period: 'CUSTOM',
        options: { from, until },
        preDefinedTimeframe: true,
      },
      label: timespan.from.format('MMMM YYYY'),
    };
  });

  return [...defaultTimeframes, ...predefinedTimeframes];
}

function renderTimeframePreviousPeriodLabel(timeframe: MetricsTimeframeInput): string {
  const previousTimeframe = convertPeriodToPreviousPeriodOption(timeframe);

  if (previousTimeframe) {
    switch (timeframe.period) {
      case 'YESTERDAY':
        return `compared to ${moment.utc(previousTimeframe.from).format('dddd, DD MMM YYYY')}`;
      case 'LAST_7_DAYS':
        return 'compared to the previous 7 days';
      case 'LAST_30_DAYS':
        return 'compared to the previous 30 days';
      case 'LAST_6_MONTHS':
        return 'compared to the previous 6 months';
      case 'CUSTOM':
        if (timeframe.category === 'preset') {
          return `compared to ${moment.utc(previousTimeframe.from).format('MMMM YYYY')}`;
        }
        return `compared to ${moment.utc(previousTimeframe.from).format('DD MMM YYYY')} - ${moment.utc(previousTimeframe.until).format('DD MMM YYYY')}`;
      default:
        return '';
    }
  }

  if (timeframe.period === 'ALL_TIME') {
    return 'Comparison is not available.';
  }

  return '';
}

export type MetricsIds = keyof DeveloperGameAnalytics | 'revenue' | 'revenuePerThousandPlays';

export interface Metrics {
  id: MetricsIds;
  name: string;
  icon: JSX.Element;
  tooltip: string;
  description: string;
  total: number | undefined;
  totalFormatting: MetricsFormatting;
  count?: number;
  countFormatting?: MetricsFormatting;
  delta?: DeltaValues | undefined;
  deltaFormatting?: MetricsFormatting;
  showGraphCard: boolean;
  graphDataFormatting?: MetricsFormatting;
  graphData?: number[] | undefined;
  graphNotes?: string;
}

export type BasicLaunchMetricsIds = 'averagePlaytime' | 'retentionD1' | 'gameplayConversion';
export type BasicLaunchMetricsDevices = 'desktop' | 'mobile';
export type BasicLaunchMetrics = {
  id: BasicLaunchMetricsIds;
  title: string;
  tooltip: string;
};
export type PrognosisLevel = {
  min: number;
  color: 'best' | 'middle' | 'worst';
  label: string;
};
export type Prognosis = {
  title?: string;
  notes?: string;
  min: number;
  max: number;
  levels: PrognosisLevel[];
};
export interface BasicLaunchMetricsSections {
  id: BasicLaunchMetricsDevices;
  title: string;
  prognosis?: Prognosis;
  totalScore: number | undefined;
  metrics: BasicLaunchMetrics[];
}

// FORMATTING
export type MetricsFormatting =
  | 'amount'
  | 'amount_compact'
  | '.0'
  | '%'
  | '.0%'
  | '.00%'
  | 's_to_00:00:00'
  | 's_to_00:00'
  | 'ms_to_00:00_minutes'
  | '0_sec'
  | '€';
export const getFormattedMetric = (value: number | null | undefined, format: MetricsFormatting) => {
  let metric;

  switch (format) {
    case 'amount':
      metric = value ? formatAmount(value) : '-';
      break;
    case 'amount_compact':
      metric = value ? i18n.number(value, { notation: 'compact' }) : '-';
      break;
    case '.0':
      metric = value ? value.toFixed(1) : '-';
      break;
    case '.0%':
      metric = value ? `${formatPercentage(value, 1)}%` : '-';
      break;
    case '.00%':
      metric = value ? `${formatPercentage(value, 2)}%` : value === 0 ? '0.00%' : '-';
      break;
    case '%':
      metric = value ? `${formatPercentage(value, 0)}%` : '-';
      break;
    case 's_to_00:00:00':
      metric = value ? formatDurationAsHMS(moment.duration(value, 'seconds')) : '-';
      break;
    case 's_to_00:00':
      metric = value ? formatDurationAsMS(moment.duration(value, 'seconds')) : '-';
      break;
    case 'ms_to_00:00_minutes':
      metric = value ? `${formatDurationAsMS(moment.duration(value, 'milliseconds'))} minutes` : '-';
      break;
    case '0_sec':
      metric = value ? `${i18n.number(value, { notation: 'compact' })} sec` : '-';
      break;
    case '€':
      metric = value ? `€ ${formatEuro(value)}` : '-';
      break;
    default:
      metric = value;
  }
  return metric;
};

export type DatesFormatting = 'DD/MM/YYYY' | 'prev3_MMM-MMM_YYYY';
function getPreviousThreeMonthsRange(date: Date): string {
  // End is last full month
  const end = new Date(date);
  end.setMonth(end.getMonth() - 1);

  // Start is two months before end
  const start = new Date(end);
  start.setMonth(start.getMonth() - 2);

  const format = (d: Date) => `${d.toLocaleString('en-US', { month: 'short' })} ${d.getFullYear()}`;

  if (start.getFullYear() === end.getFullYear()) {
    // Same year: May-Jul 2025
    const startMonth = start.toLocaleString('en-US', { month: 'short' });
    const endMonth = end.toLocaleString('en-US', { month: 'short' });
    return `${startMonth}-${endMonth} ${end.getFullYear()}`;
  } else {
    // Cross-year: Dec 2025 - Feb 2026
    return `${format(start)} - ${format(end)}`;
  }
}

export const getFormattedDate = (value: string | null | undefined, format: DatesFormatting) => {
  let FORMATTED_DATE;
  const DATE = value && new Date(value);

  switch (format) {
    case 'prev3_MMM-MMM_YYYY':
      FORMATTED_DATE = DATE && getPreviousThreeMonthsRange(DATE);
      break;
    case 'DD/MM/YYYY':
    default:
      const day = DATE && DATE.getUTCDate().toString().padStart(2, '0');
      const month = DATE && (DATE.getUTCMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
      const year = DATE && DATE.getUTCFullYear();
      FORMATTED_DATE = `${day}/${month}/${year}`;
  }

  return FORMATTED_DATE;
};

export const isBeforeToday = (targetDate: string | null): boolean => {
  if (!targetDate) return false;

  const parsedTargetDate = moment.utc(targetDate, GRAPHQL_DATETIME_FORMAT);
  const today = moment.utc();

  return today.isAfter(parsedTargetDate);
};

export const isAfterDate = (targetDate: string | null, referenceDate: string | null): boolean => {
  if (!targetDate || !referenceDate) return false;

  const parsedTargetDate = moment.utc(targetDate, GRAPHQL_DATETIME_FORMAT);
  const parsedReferenceDate = moment.utc(referenceDate, GRAPHQL_DATETIME_FORMAT);

  return parsedTargetDate.isAfter(parsedReferenceDate);
};

export const isPastByNDays = (date: string | null, days: number): boolean => {
  const numDaysDifference = moment.utc().diff(moment.utc(date, GRAPHQL_DATETIME_FORMAT), 'days');
  return numDaysDifference > days;
};

export type DeltaValues = {
  previous: number;
  absoluteDifference: number;
  relativeDifference: number;
  isInverted: boolean;
};

function calculateDelta(value: number, previous: number, isInverted: boolean): DeltaValues {
  const absoluteDifference = value - previous;
  const relativeDifference = previous !== 0 ? absoluteDifference / previous : 1;
  return { previous, absoluteDifference, relativeDifference, isInverted };
}

export const hasImproved = (delta: DeltaValues) => {
  if (delta.isInverted) {
    return delta.relativeDifference < 0;
  }
  return delta.relativeDifference > 0;
};

export interface TotalData {
  helpers: {
    deltaLabel?: string;
  };
  totals: {
    selectedData: Record<MetricsIds, number | undefined>;
    previousData: Record<MetricsIds, number | undefined>;
    deltas: Record<MetricsIds, DeltaValues>;
  };
}

export const getAggregatedAnalytics = (
  data: MyGameStatsQuery | undefined,
  previousData: MyGameStatsQuery | undefined,
  timeframe: MetricsTimeframeInput,
): TotalData => {
  const aggregatedAnalytics: TotalData = {
    helpers: {},
    totals: {
      selectedData: {} as Record<MetricsIds, number>,
      previousData: {} as Record<MetricsIds, number>,
      deltas: {} as Record<MetricsIds, DeltaValues>,
    },
  };

  if (!data) return aggregatedAnalytics; // nothing to aggregate

  const analytics = data.gameBySlug.devStats.analytics;
  const previousAnalytics = previousData?.gameBySlug.devStats.analytics;

  const deltaLabel = renderTimeframePreviousPeriodLabel(timeframe);
  aggregatedAnalytics.helpers['deltaLabel'] = deltaLabel;

  // Base metrics
  (Object.entries(analytics) as [keyof DeveloperGameAnalytics, number][]).forEach(([key, value]) => {
    aggregatedAnalytics.totals.selectedData[key] = value;

    const prevValue = previousAnalytics?.[key];
    aggregatedAnalytics.totals.previousData[key] = prevValue ?? 0;

    if (previousData) {
      aggregatedAnalytics.totals.deltas[key] = calculateDelta(value, prevValue ?? 0, key === 'avgLoadingTime');
    }
  });

  // Revenue
  const REVENUE = data.gameBySlug.devStats.revenueShare.total;
  const PREVIOUS_PERIOD_REVENUE = previousData?.gameBySlug.devStats.revenueShare.total ?? 0;

  aggregatedAnalytics.totals.selectedData['revenue'] = REVENUE;
  aggregatedAnalytics.totals.previousData['revenue'] = PREVIOUS_PERIOD_REVENUE;
  if (previousData) {
    aggregatedAnalytics.totals.deltas['revenue'] = calculateDelta(REVENUE, PREVIOUS_PERIOD_REVENUE, false);
  }

  // Revenue per 1k plays
  const REVENUE_1K_PLAYS = analytics.pageviews ? (REVENUE / analytics.pageviews) * 1000 : 0;
  const PREVIOUS_REVENUE_1K_PLAYS = previousAnalytics?.pageviews ? (PREVIOUS_PERIOD_REVENUE / previousAnalytics.pageviews) * 1000 : 0;

  aggregatedAnalytics.totals.selectedData['revenuePerThousandPlays'] = REVENUE_1K_PLAYS;
  aggregatedAnalytics.totals.previousData['revenuePerThousandPlays'] = PREVIOUS_REVENUE_1K_PLAYS;
  if (previousData) {
    aggregatedAnalytics.totals.deltas['revenuePerThousandPlays'] = calculateDelta(REVENUE_1K_PLAYS, PREVIOUS_REVENUE_1K_PLAYS, false);
  }

  return aggregatedAnalytics;
};

export type DailyRawData = {
  pageviews: number[];
  users: number[];
  impressions: number[];
  ctr: number[];
  avgTimeOnPage: number[];
  avgConversion: number[];
  retentionD1: number[];
  retentionD7: number[];
  returningPlayers: number[];
  avgLoadingTime: number[];
  issues: number[];
  revenue: number[];
  revenuePerThousandPlays: number[];
  iap: number[];
  rating: number[];
};

export type DailyLabels = {
  stats: string[];
  monetization: string[];
};

export const getDailyRawData = (
  data: MyGameDailyStatsQuery | undefined,
  dateRange: string[], // Add this as a new parameter
): DailyRawData | undefined => {
  const dailyRawData: DailyRawData = {
    pageviews: [],
    users: [],
    impressions: [],
    ctr: [],
    avgTimeOnPage: [],
    avgConversion: [],
    retentionD1: [],
    retentionD7: [],
    returningPlayers: [],
    avgLoadingTime: [],
    issues: [],
    revenue: [],
    revenuePerThousandPlays: [],
    iap: [],
    rating: [],
  };

  if (!data) return undefined;

  // Step 1: Map dailyStats by date for fast lookup
  const statsMap = new Map(data.gameBySlug.dailyStats?.map((stat) => [stat.date, stat]) ?? []);

  // Step 2: Loop through the expected date range
  dateRange.forEach((date) => {
    const stat = statsMap.get(date);

    dailyRawData.pageviews.push(stat?.pageviews ?? 0);
    dailyRawData.users.push(stat?.users ?? 0);
    dailyRawData.impressions.push(stat?.impressions ?? 0);
    dailyRawData.ctr.push((stat?.ctr ?? 0) * 100);
    dailyRawData.avgTimeOnPage.push(stat?.avgTimeOnPage ?? 0);
    dailyRawData.avgConversion.push((stat?.avgConversion ?? 0) * 100);
    dailyRawData.retentionD1.push((stat?.retentionD1 ?? 0) * 100);
    dailyRawData.retentionD7.push((stat?.retentionD7 ?? 0) * 100);
    dailyRawData.returningPlayers.push((stat?.returningPlayers ?? 0) * 100);
    dailyRawData.avgLoadingTime.push(stat?.avgLoadingTime ?? 0);
    dailyRawData.issues.push(stat?.issues ?? 0);
    dailyRawData.rating.push(stat?.rating ?? 0);
  });

  // Step 3: Map dailyRevenueStats by date
  const revenueMap = new Map(data.gameBySlug.dailyRevenueStats.map((stat) => [stat.date, stat]));

  dateRange.forEach((date) => {
    const revenue = revenueMap.get(date);
    dailyRawData.revenue.push(revenue?.revenueShareDaily.total ?? 0);
    dailyRawData.iap.push(revenue?.revenueShareDaily.iap ?? 0);

    const stat = statsMap.get(date);
    dailyRawData.revenuePerThousandPlays.push(((revenue?.revenueShareDaily.total ?? 0) / (stat?.pageviews ?? 0)) * 1000);
  });

  return dailyRawData;
};

export const getDailyLabels = (from: string, until: string): string[] | undefined => {
  const FROM = new Date(from);
  const UNTIL = until && new Date(until);

  const labels = [];
  const currentDate = new Date(FROM);

  while (currentDate <= UNTIL) {
    labels.push(currentDate.toISOString().split('T')[0]);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return labels;
};

export const getBasicLaunchData = (data: BasicLaunchStatsAndBenchmarks) => {
  function getAchievedScore(metric: number, benchmarks: number[]) {
    for (let i = 0; i < benchmarks.length; i++) {
      if (metric <= benchmarks[i]) {
        return i + 1;
      }
    }
    return benchmarks.length + 1;
  }

  function getNextScore(metric: number, benchmarks: number[], score: number, nextScoreFormat: MetricsFormatting) {
    const nextBenchmark = benchmarks[score - 1];
    const nextScore = score + 1;

    if (score === benchmarks.length + 1) {
      return '🎊 Your game has the top score!';
    }

    if (metric === nextBenchmark) {
      return `Surpass ${getFormattedMetric(nextBenchmark, nextScoreFormat)} to score ${nextScore}`;
    }

    return `Score ${nextScore} at ${getFormattedMetric(nextBenchmark, nextScoreFormat)}`;
  }

  const buildMetricData = (metric: number, benchmarks: number[], format: MetricsFormatting) => {
    const score = getAchievedScore(metric, benchmarks);
    const nextScore = getNextScore(metric, benchmarks, score, format);

    return { total: getFormattedMetric(metric, format), score, nextScore };
  };

  const buildPlatformData = (platform: BasicLaunchPerformanceIndicators) => {
    if (!platform.benchmarks.averagePlaytime || !platform.benchmarks.gameplayConversion || !platform.benchmarks.retentionD1) {
      return null;
    }

    const averagePlaytime = buildMetricData(platform.metrics.averagePlaytime, platform.benchmarks.averagePlaytime, 'ms_to_00:00_minutes');
    const gameplayConversion = buildMetricData(platform.metrics.gameplayConversion, platform.benchmarks.gameplayConversion, '.00%');
    const retentionD1 = buildMetricData(platform.metrics.retentionD1, platform.benchmarks.retentionD1, '.00%');

    const totalScore = averagePlaytime.score + gameplayConversion.score + retentionD1.score;

    return {
      averagePlaytime,
      gameplayConversion,
      retentionD1,
      totalScore,
    };
  };

  return {
    desktop: buildPlatformData(data.desktop),
    mobile: buildPlatformData(data.mobile),
  };
};

export const sumArrayValues = (arr: number[]) => {
  return arr.reduce((acc, val) => acc + val, 0);
};
